import { Toggle } from '../../../shared/components/Toggle';
import { useBranches, useBranchExclusions, useSetBranchAvailability } from '../hooks/useBranches';
import type { BranchExclusionEntity } from '../api/branchesApi';

interface Props {
  /** Which catalogue entity this editor is attached to. */
  entity: BranchExclusionEntity;
  entityId: string | null | undefined;
  merchantId: string | null | undefined;
  /** Read-only when the signed-in admin lacks branch-manage. */
  disabled?: boolean;
}

/**
 * Per-branch availability for one catalogue entity (PE-2139).
 *
 * ⚠️ The backend stores EXCLUSIONS: an entity is available at every branch by default and
 * a stored row means "not available here". So a toggle that is ON means *no* exclusion row
 * exists. Writes are therefore inverted — switching a branch off POSTs an exclusion, and
 * switching it back on DELETEs it.
 *
 * Shared deliberately across products, booking services and package listings, since the
 * backend exposes the identical three verbs for each.
 */
export function BranchAvailabilityEditor({ entity, entityId, merchantId, disabled = false }: Props) {
  const { data: branches = [], isLoading: branchesLoading } = useBranches(merchantId);
  const { data: excludedIds = [], isLoading: exclusionsLoading } = useBranchExclusions(entity, entityId);
  const setAvailability = useSetBranchAvailability(entity, entityId);

  // Deactivated branches aren't valid sales locations, so don't offer them here.
  const activeBranches = branches.filter((b) => b.isActive);
  const isLoading = branchesLoading || exclusionsLoading;

  if (!merchantId) {
    return (
      <p className="text-[13px] text-muted-foreground">Select a merchant to manage branch availability.</p>
    );
  }

  if (isLoading) {
    return <p className="text-[13px] text-muted-foreground">Loading branches…</p>;
  }

  if (activeBranches.length === 0) {
    return (
      <p className="text-[13px] text-muted-foreground">
        This merchant has no active branches, so this item is available everywhere by default.
      </p>
    );
  }

  const excluded = new Set(excludedIds);

  return (
    <div className="space-y-3">
      <p className="text-[13px] text-muted-foreground">
        Available at every branch by default. Switch a branch off to make this item unavailable there.
      </p>
      <div className="divide-y divide-border rounded-[12px] border border-border">
        {activeBranches.map((branch) => {
          const available = !excluded.has(branch.id);
          return (
            <div key={branch.id} className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0">
                <p className="text-[14px] font-[600] text-foreground truncate">{branch.name}</p>
                {branch.address && (
                  <p className="text-[12px] text-muted-foreground truncate">{branch.address}</p>
                )}
              </div>
              <div className={disabled ? 'pointer-events-none opacity-50' : ''}>
                <Toggle
                  checked={available}
                  onChange={(checked) =>
                    setAvailability.mutate({ branchId: branch.id, available: checked })
                  }
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
